import {Static, TSchema, Type} from '@sinclair/typebox';

export type BuildEnvironment =
  | 'development'
  | 'staging'
  | 'production'
  | 'local';

export const TTimestamp = Type.Recursive((TThis) =>
  Type.Object(
    {
      seconds: Type.Readonly(Type.Number()),
      nanoseconds: Type.Readonly(Type.Number()),
      toDate: Type.Function([], Type.Date()),
      toMillis: Type.Function([], Type.Number()),
      isEqual: Type.Function([TThis], Type.Boolean()),
      valueOf: Type.Function([], Type.String()),
    },
    {default: undefined},
  ),
);
export type TTimestamp = Static<typeof TTimestamp>;

export const MAX_USER_NAME_LENGTH = 25;
export const MAX_USER_TAGLINE_LENGTH = 50;
export const MAX_USER_TEAM_LENGTH = 50;
export const MAX_USER_HOMETOWN_LENGTH = 25;
export const MAX_GROUP_NAME_LENGTH = 25;

export const TImageMediaEntry = Type.Object({
  image: Type.Optional(
    Type.Object({
      url: Type.String(),
    }),
  ),
  caption: Type.Optional(Type.String()),
});
export type TImageMediaEntry = Static<typeof TImageMediaEntry>;

export const TVideoMediaEntry = Type.Object({
  video: Type.Optional(
    Type.Object({
      url: Type.String(),
    }),
  ),
  caption: Type.Optional(Type.String()),
});
export type TVideoMediaEntry = Static<typeof TVideoMediaEntry>;

export const TSocialMediaEntry = Type.Object({
  social: Type.Optional(
    Type.Object({
      embed: Type.String(),
    }),
  ),
  caption: Type.Optional(Type.String()),
});
export type TSocialMediaEntry = Static<typeof TSocialMediaEntry>;

export const TMediaEntry = Type.Composite([
  TImageMediaEntry,
  TVideoMediaEntry,
  TSocialMediaEntry,
]);
export type TMediaEntry = Static<typeof TMediaEntry>;

export const TLeader = Type.Object({
  u: Type.String(), // userId
  s: Type.Number(), // score
  o: Type.Number(), // sort
  d: Type.Optional(Type.Number()), // position change on last rank
  w: Type.Optional(Type.Boolean()), // winner
});
export type TLeader = Static<typeof TLeader>;

export const TMultiGameLeader = Type.Composite([
  TLeader,
  Type.Object({
    n: Type.Number(), // number of comps
    t: Type.Number(), // total points
    f: Type.Number(), // number of top tier finishes
  }),
]);
export type TMultiGameLeader = Static<typeof TMultiGameLeader>;

const TLeadersSchema = <L extends TSchema>(schema: L) =>
  Type.Object({leaders: Type.Array(schema)});
export const TLeaders = TLeadersSchema(TLeader);
export type TLeaders = Static<typeof TLeaders>;
export const TMultiGameLeaders = TLeadersSchema(TMultiGameLeader);
export type TMultiGameLeaders = Static<typeof TMultiGameLeaders>;

export enum LeaderboardType {
  CHANCER = 'chancer',
  MULTIGAME = 'multigame',
  INGAME = 'ingame',
  BEST_2 = 'best2',
  BEST_3 = 'best3',
  BEST_4 = 'best4',
  BEST_5 = 'best5',
  BEST_6 = 'best6',
  BEST_7 = 'best7',
  BEST_8 = 'best8',
  BEST_9 = 'best9',
  BEST_10 = 'best10',
  // All star must start with allstar_ to be picked up by the views correctly
  ALL_STAR_6_13_20 = 'allstar_6_13_20',
  ALL_STAR_2_5_8 = 'allstar_2_5_8',
}
export const TLeaderboardType = Type.Enum(LeaderboardType, {
  default: LeaderboardType.CHANCER,
});

export const TLeaderboardLevelDetails = Type.Union([
  Type.String(),
  Type.Object({
    open: Type.Optional(Type.String()),
    inPlay: Type.Optional(Type.String()),
    resulted: Type.Optional(Type.String()),
  }),
]);
export type TLeaderboardLevelDetails = Static<typeof TLeaderboardLevelDetails>;

export const TLeaderboardLevel = Type.Object({
  title: Type.String(),
  details: Type.Optional(TLeaderboardLevelDetails),
});
export type TLeaderboardLevel = Static<typeof TLeaderboardLevel>;

export const TLeaderboardPrize = Type.Composite([
  TLeaderboardLevel,
  Type.Object({
    media: TMediaEntry,
  }),
]);
export type TLeaderboardPrize = Static<typeof TLeaderboardPrize>;

export const TUserCompletedLeaderboard = Type.Object({
  id: Type.String(),
  type: TLeaderboardType,
  points: Type.Number(),
  position: Type.Number(),
  players: Type.Number(),
  winner: Type.Optional(Type.Boolean()),
});
export type TUserCompletedLeaderboard = Static<
  typeof TUserCompletedLeaderboard
>;

export const TUserCompletedComp = Type.Object({
  compId: Type.String(),
  leaderboards: Type.Array(TUserCompletedLeaderboard),
});
export type TUserCompletedComp = Static<typeof TUserCompletedComp>;

export const TUserCompletedChallenge = Type.Object({
  compId: Type.String(),
  challengeId: Type.String(),
  users: Type.Array(Type.String()),
  result: Type.Array(Type.Number()),
  streak: Type.Number(),
  longestStreak: Type.Array(Type.Number()),
  streakVendor: Type.Number(),
  longestStreakVendor: Type.Array(Type.Number()),
  wins: Type.Array(Type.Number()),
  winsVendor: Type.Array(Type.Number()),
  totalPlayed: Type.Number(),
  totalPlayedVendor: Type.Number(),
});
export type TUserCompletedChallenge = Static<typeof TUserCompletedChallenge>;

export const TUserCompletedAchievement = Type.Object({
  compId: Type.String(),
  score: Type.Number(),
  achievements: Type.Array(Type.Number()),
});
export type TUserCompletedAchievement = Static<
  typeof TUserCompletedAchievement
>;

export const TUserPassedThreshold = Type.Object({
  compId: Type.String(),
  threshold: Type.Number(),
  title: Type.String(),
  subtitle: Type.String(),
  description: Type.String(),
  media: TMediaEntry,
  prizes: Type.Array(TLeaderboardPrize),
});
export type TUserPassedThreshold = Static<typeof TUserPassedThreshold>;

export const TUserCompletedMiniLeague = Type.Object({
  id: Type.String(),
  type: TLeaderboardType,
  compIds: Type.Array(Type.String()),
  stars: Type.Number(),
  otherPlayersWithSameStars: Type.Number(),
  levelTitle: Type.String(),
  points: Type.Number(),
  position: Type.Number(),
  players: Type.Number(),
  wins: Type.Number(),
  winner: Type.Optional(Type.Boolean()),
});
export type TUserCompletedMiniLeague = Static<typeof TUserCompletedMiniLeague>;

export const TUserOptionalData = Type.Object({
  completedComps: Type.Optional(Type.Array(TUserCompletedComp)),
  completedChallenges: Type.Optional(Type.Array(TUserCompletedChallenge)),
  completedAchievements: Type.Optional(Type.Array(TUserCompletedAchievement)),
  completedMiniLeagues: Type.Optional(Type.Array(TUserCompletedMiniLeague)),
  passedThresholds: Type.Optional(Type.Array(TUserPassedThreshold)),
  achievementsNew: Type.Optional(Type.Array(Type.Number())),
  vendorAchievementsNew: Type.Optional(
    Type.Record(Type.String(), Type.Array(Type.Number())),
  ),
  hometown: Type.Optional(Type.String()),
  tagline: Type.Optional(Type.String()),
  team: Type.Optional(Type.String()),
});
export type TUserOptionalData = Static<typeof TUserOptionalData>;

export const TUser = Type.Composite([
  TUserOptionalData,
  Type.Object({
    created: Type.Optional(TTimestamp),
    updated: Type.Optional(TTimestamp),
    name: Type.String(),
    media: Type.Optional(TMediaEntry),
    achievements: Type.Array(Type.Number()),
    vendorAchievements: Type.Optional(
      Type.Record(Type.String(), Type.Array(Type.Number())),
    ),
    vendorLeaders: Type.Optional(Type.Record(Type.String(), TLeader)),
    vipVendors: Type.Optional(Type.Array(Type.String())),
    instruction: Type.Optional(Type.String()),
    temporary: Type.Optional(Type.Boolean()),
  }),
]);
export type TUser = Static<typeof TUser>;

export const TUserPrivateProfile = Type.Object({
  updated: Type.Optional(TTimestamp),
  subscriptions: Type.Object({
    emailUpdates: Type.Union([Type.Boolean(), Type.Undefined()]),
    emailAlerts: Type.Union([Type.Boolean(), Type.Undefined()]),
    emailPromotions: Type.Union([Type.Boolean(), Type.Undefined()]),
    sms: Type.Union([Type.Boolean(), Type.Undefined()]),
  }),
  fcmRegistered: Type.Union([Type.Boolean(), Type.Undefined()]),
  fcmTokens: Type.Array(Type.String()),
  disabledLeagues: Type.Optional(Type.Array(Type.String())),
  temporaryUserScore: Type.Optional(Type.Number()),
});
export type TUserPrivateProfile = Static<typeof TUserPrivateProfile>;

export const TUserInternalPrivateProfile = Type.Object({
  updated: Type.Optional(TTimestamp),
  notes: Type.Optional(Type.String()),
});
export type TUserInternalPrivateProfile = Static<
  typeof TUserInternalPrivateProfile
>;

export const TUserFollowList = Type.Object({
  userIds: Type.Array(Type.String()),
});
export type TUserFollowList = Static<typeof TUserFollowList>;

export const TCompLogo = Type.Composite([
  TImageMediaEntry,
  Type.Object({description: Type.Optional(Type.String())}),
]);
export type TCompLogo = Static<typeof TCompLogo>;

export const TCompBase = Type.Object({
  featured: Type.Boolean(),
  primaryColor: Type.String({default: '#000000'}),
  secondaryColor: Type.String({default: '#000000'}),
  teamColor: Type.String({default: '#000000'}),
  sport: Type.String(),
  league: Type.String(),
  starts: TTimestamp,
  closes: TTimestamp,
  name: Type.String(),
  shortName: Type.String(),
  roundName: Type.String(),
  logo: TCompLogo,
  sponsor: TImageMediaEntry,
  media: TMediaEntry,
  promo: TMediaEntry,
  feature: Type.Optional(TMediaEntry),
  vendor: Type.String(),
  teamId: Type.Optional(Type.String()),
  key: Type.String(),
  strapline: Type.Optional(Type.String()),
  tags: Type.Optional(Type.Array(Type.String())),
});
export type TCompBase = Static<typeof TCompBase>;

export enum CompStatus {
  DRAFT = 'DRAFT',
  OPEN = 'OPEN',
  SUSPENDED = 'SUSPENDED',
  INPLAY = 'INPLAY',
  RESULTED = 'RESULTED',
  VERIFIED = 'VERIFIED',
  AWARDED = 'AWARDED',
  NOTIFIED = 'NOTIFIED',
}
export const TCompStatus = Type.Enum(CompStatus, {default: CompStatus.DRAFT});

const TLeaderboardBase = Type.Object({
  published: Type.Optional(Type.Boolean()),
  type: TLeaderboardType,
  key: Type.String(),
  isMiniLeague: Type.Optional(Type.Boolean()),
  shortName: Type.String(),
  name: Type.String(),
  privateCaption: Type.Optional(Type.String()),
  media: TMediaEntry,
  prizes: Type.Array(TLeaderboardPrize),
  winScoreThresholds: Type.Optional(
    Type.Union([Type.Number(), Type.Array(Type.Number())]),
  ),
});
type TLeaderboardBase = Static<typeof TLeaderboardBase>;

export const TLeaderboardSummary = Type.Composite([
  TLeaderboardBase,
  Type.Object({
    id: Type.String(),
  }),
]);
export type TLeaderboardSummary = Static<typeof TLeaderboardSummary>;

export const TLeaderboardSummaryAndLeaders = Type.Composite([
  TLeaderboardSummary,
  TLeaders,
  Type.Object({totalLeadersCount: Type.Number()}),
]);
export type TLeaderboardSummaryAndLeaders = Static<
  typeof TLeaderboardSummaryAndLeaders
>;

export const TLiveScoreBase = Type.Object({
  homeName: Type.String(),
  awayName: Type.String(),
  homeScore: Type.Number(),
  awayScore: Type.Number(),
});
export type TLiveScoreBase = Static<typeof TLiveScoreBase>;

export enum LiveScorePeriod {
  DEFAULT = 'DEFAULT',
  Q1 = 'Q1',
  Q2 = 'Q2',
  Q3 = 'Q3',
  Q4 = 'Q4',
  H1 = 'H1',
  H2 = 'H2',
  HT = 'HT',
  OT = 'OT',
}
export const TLiveScorePeriod = Type.Enum(LiveScorePeriod, {
  default: LiveScorePeriod.DEFAULT,
});

export const TLiveScoreSummary = Type.Composite([
  TLiveScoreBase,
  Type.Object({
    isLive: Type.Boolean(),
    updated: TTimestamp,
    period: Type.Optional(TLiveScorePeriod),
    periodSeconds: Type.Optional(Type.Number()),
  }),
]);
export type TLiveScoreSummary = Static<typeof TLiveScoreSummary>;

export const TCompSummary = Type.Composite([
  TCompBase,
  Type.Object({
    status: TCompStatus,
    entriesCount: Type.Number(),
    openQuestions: Type.Array(Type.String()),
    inPlayQuestions: Type.Array(Type.String()),
    leaderboards: Type.Array(TLeaderboardSummary),
    liveScore: Type.Optional(TLiveScoreSummary),
  }),
]);
export type TCompSummary = Static<typeof TCompSummary>;

export const TCompCompleteView = Type.Object({
  media: TMediaEntry,
});
export type TCompCompleteView = Static<typeof TCompCompleteView>;

export const TSignUpCompleteView = Type.Object({
  media: TMediaEntry,
  description: Type.Optional(Type.String()),
  webDescription: Type.Optional(Type.String()),
});
export type TSignUpCompleteView = Static<typeof TSignUpCompleteView>;

export const TOpenGraphMedia = Type.Composite([
  TImageMediaEntry,
  Type.Object({
    title: Type.Optional(Type.String()),
  }),
]);
export type TOpenGraphMedia = Static<typeof TOpenGraphMedia>;

export const TCompShareDetails = Type.Object({
  openGraph: TOpenGraphMedia,
  shareText: Type.String(),
});
export type TCompShareDetails = Static<typeof TCompShareDetails>;

export const TComp = Type.Composite([
  TCompBase,
  Type.Object({
    created: TTimestamp,
    modified: Type.Optional(TTimestamp),
    details: Type.String(),
    completeView: TCompCompleteView,
    signUpCompleteView: TSignUpCompleteView,
    shareDetails: TCompShareDetails,
    leaderboards: Type.Array(Type.String()),
    published: Type.Boolean(),
    chat: Type.Boolean(),
    shortUrl: Type.String(),
    qrCode: Type.Optional(Type.String()),
    shortUrlPlay: Type.String(),
    qrCodePlay: Type.Optional(Type.String()),
    template: Type.Boolean({default: false}),
    buildModel: Type.Optional(Type.Union([Type.Literal('chancer'), Type.Literal('talkingPoints')])),
  }),
]);
export type TComp = Static<typeof TComp>;

export const TNotificationSent = Type.Object({
  gameOpen: Type.Optional(TTimestamp),
  gameInPlay: Type.Optional(TTimestamp),
  gameReminder: Type.Optional(TTimestamp),
  gameOutstandingQuestions: Type.Optional(TTimestamp),
  gameVerified: Type.Optional(TTimestamp),
  gamePrize: Type.Optional(TTimestamp),
  gameManual: Type.Optional(Type.Record(Type.String(), TTimestamp)),
});
export type TNotificationSent = Static<typeof TNotificationSent>;

export const TCompNotifications = Type.Object({
  templateGameVerified: Type.Union([Type.String(), Type.Null()]),
  templateGameInPlay: Type.Union([Type.String(), Type.Null()]),
  templateGameEntry: Type.Union([Type.String(), Type.Null()]),
  templateGamePrize: Type.Union([Type.String(), Type.Null()]),
  templateGameOutstandingQuestions: Type.Union([Type.String(), Type.Null()]),
  templateGameOpen: Type.Union([Type.String(), Type.Null()]),
  templateGamePreStart: Type.Union([Type.String(), Type.Null()]),
});
export type TCompNotifications = Static<typeof TCompNotifications>;

export const TEmailNotifications = Type.Composite([
  TCompNotifications,
  Type.Object({
    vendorListId: Type.Union([Type.String(), Type.Null()]),
    categoriesGameVerified: Type.Union([
      Type.Array(Type.String()),
      Type.Null(),
    ]),
    categoriesGameInPlay: Type.Union([Type.Array(Type.String()), Type.Null()]),
    categoriesGameEntry: Type.Union([Type.Array(Type.String()), Type.Null()]),
    categoriesGamePrize: Type.Union([Type.Array(Type.String()), Type.Null()]),
    categoriesGameOutstandingQuestions: Type.Union([
      Type.Array(Type.String()),
      Type.Null(),
    ]),
    categoriesGameOpen: Type.Union([Type.Array(Type.String()), Type.Null()]),
    categoriesGamePreStart: Type.Union([
      Type.Array(Type.String()),
      Type.Null(),
    ]),
  }),
]);
export type TEmailNotifications = Static<typeof TEmailNotifications>;

export const TNativeNotification = Type.Object({
  title: Type.String(),
  body: Type.String(),
});
export type TNativeNotification = Static<typeof TNativeNotification>;

export const TNativeNotifications = Type.Object({
  templateGameOpen: Type.Union([TNativeNotification, Type.Null()]),
  templateGamePreStart: Type.Union([TNativeNotification, Type.Null()]),
  templateGameOutstandingQuestions: Type.Union([
    TNativeNotification,
    Type.Null(),
  ]),
  templateGameInPlay: Type.Union([TNativeNotification, Type.Null()]),
  templateGameVerified: Type.Union([TNativeNotification, Type.Null()]),
  templateGamePrize: Type.Union([TNativeNotification, Type.Null()]),
});
export type TNativeNotifications = Static<typeof TNativeNotifications>;

export enum LiveScoreProvider {
  GENIUS = 'GENIUS',
  FLASHSCORE = 'FLASHSCORE',
  GOOGLE = 'GOOGLE',
  SOFASCORE = 'SOFASCORE',
  RADAR = 'RADAR',
  TABNZ = 'TABNZ',
  NRL = 'NRL',
  WORLD_RUGBY = 'WORLD_RUGBY',
  RUGBYPASS = 'RUGBYPASS',
  ESPN = 'ESPN',
  PULSELIVE = 'PULSELIVE',
  SPORTRADAR_EUI = 'SPORTRADAR_EUI',
}
export const TLiveScoreProvider = Type.Enum(LiveScoreProvider, {
  default: LiveScoreProvider.GENIUS,
});

export const TLiveScorePlayer = Type.Object({
  playerId: Type.String(),
  name: Type.Optional(Type.String()),
  score: Type.Number(),
  starter: Type.Optional(Type.Boolean()),
  minutes: Type.Optional(Type.Number()),
  /** Jersey number (e.g. 1, 6, 13). Populated from knowledge-base or feed. */
  number: Type.Optional(Type.Number()),
  /** Playing position (e.g. "Fullback", "Five-Eighth", "Lock"). */
  position: Type.Optional(Type.String()),
  /** Abbreviated display name (e.g. "R. Tuivasa-Sheck"). */
  shortName: Type.Optional(Type.String()),
  tries: Type.Optional(Type.Number()),
  runMetres: Type.Optional(Type.Number()),
  tacklesMade: Type.Optional(Type.Number()),
  /**
   * Football. `score` carries goals so the scorer questions can rank a player
   * list without knowing the code, but a goal is not a try and reading one as
   * the other in a caption would be plainly wrong, so it is also named.
   */
  goals: Type.Optional(Type.Number()),
  assists: Type.Optional(Type.Number()),
  shotsOnTarget: Type.Optional(Type.Number()),
  saves: Type.Optional(Type.Number()),
});
export type TLiveScorePlayer = Static<typeof TLiveScorePlayer>;

export const TLiveScorePeriodScore = Type.Object({
  period: TLiveScorePeriod,
  homePeriodScore: Type.Number(),
  homeAccumulatedScore: Type.Number(),
  awayPeriodScore: Type.Number(),
  awayAccumulatedScore: Type.Number(),
});
export type TLiveScorePeriodScore = Static<typeof TLiveScorePeriodScore>;

export const TLiveScoreConfiguration = Type.Object({
  enabled: Type.Boolean(),
  scheduleMatchId: Type.Optional(Type.String()),
  scheduleLeagueId: Type.Optional(Type.String()),
  provider: Type.Optional(TLiveScoreProvider),
  matchId: Type.Optional(Type.String()),
  autoResolve: Type.Boolean({default: false}),
  autoResultComp: Type.Boolean({default: false}),
  autoVerifyComp: Type.Boolean({default: false}),
  winnerMargin: Type.Optional(Type.Number()),
  firstToScoreX: Type.Optional(Type.Number()),
  homeAveragePlayers: Type.Optional(Type.Array(TLiveScorePlayer)),
  awayAveragePlayers: Type.Optional(Type.Array(TLiveScorePlayer)),
  homeLastPlayers: Type.Optional(Type.Array(TLiveScorePlayer)),
  awayLastPlayers: Type.Optional(Type.Array(TLiveScorePlayer)),
  statsHomeToScoreX: Type.Optional(Type.Number()),
  statsAwayToScoreX: Type.Optional(Type.Number()),
  statsTotalOverUnder: Type.Optional(Type.Number()),
  totalBands: Type.Optional(Type.Array(Type.Number())),
  homeName: Type.Optional(Type.String()),
  homeCode: Type.Optional(Type.String()),
  awayName: Type.Optional(Type.String()),
  awayCode: Type.Optional(Type.String()),
  nrlMatchCentreUrl: Type.Optional(Type.String()),
  rugbyPassMatchUrl: Type.Optional(Type.String()),
});
export type TLiveScoreConfiguration = Static<typeof TLiveScoreConfiguration>;

export const TLiveScorePrivate = Type.Composite([
  TLiveScoreBase,
  Type.Object({
    finished: Type.Boolean(),
    homePlayers: Type.Optional(Type.Array(TLiveScorePlayer)),
    awayPlayers: Type.Optional(Type.Array(TLiveScorePlayer)),
    period: Type.Optional(TLiveScorePeriod),
    periodScores: Type.Optional(Type.Array(TLiveScorePeriodScore)),
  }),
]);
export type TLiveScorePrivate = Static<typeof TLiveScorePrivate>;

export const TCompPrivate = Type.Object({
  notificationsSent: Type.Optional(TNotificationSent),
  emailNotifications: TEmailNotifications,
  smsNotifications: TCompNotifications,
  nativeNotifications: TNativeNotifications,
  autoInPlay: Type.Optional(Type.Boolean()),
  prizeCode: Type.Optional(Type.String()),
  liveScoreConfiguration: Type.Optional(TLiveScoreConfiguration),
  liveScore: Type.Optional(TLiveScorePrivate),
});
export type TCompPrivate = Static<typeof TCompPrivate>;

export enum QuestionStatus {
  DRAFT = 'DRAFT',
  OPEN = 'OPEN',
  OPEN_CHANGEABLE = 'OPEN_CHANGEABLE',
  INPLAY = 'INPLAY',
  RESOLVED = 'RESOLVED',
}
export const TQuestionStatus = Type.Enum(QuestionStatus, {
  default: QuestionStatus.DRAFT,
});

export const TJunctionCompetitionsQuestions = Type.Object({
  competitionId: Type.String(),
  questionId: Type.String(),
  questionIndex: Type.Number(),
  status: TQuestionStatus,
});
export type TJunctionCompetitionsQuestions = Static<
  typeof TJunctionCompetitionsQuestions
>;

export const TCompAnswer = Type.Object({
  label: Type.String(),
  autoResolveId: Type.Optional(Type.String()),
});
export type TCompAnswer = Static<typeof TCompAnswer>;

export const TCompQuestionAuthor = Type.Optional(TImageMediaEntry);
export type TCompQuestionAuthor = Static<typeof TCompQuestionAuthor>;

export const TCompQuestionLink = Type.Composite([
  TImageMediaEntry,
  TVideoMediaEntry,
  Type.Object({
    url: Type.String(),
    text: Type.String(),
    publisher: Type.Optional(TImageMediaEntry),
  }),
]);
export type TCompQuestionLink = Static<typeof TCompQuestionLink>;

export enum CompQuestionPublishingType {
  WINNER_POLL = 'winner_poll',
  WINNING_MARGIN = 'winning_margin',
  PLAYER_OF_MATCH = 'player_of_match',
}
export const TCompQuestionPublishingType = Type.Enum(
  CompQuestionPublishingType,
  {
    default: CompQuestionPublishingType.WINNER_POLL,
  },
);

export enum CompQuestionTemplateType {
  DEFAULT = 'default',
  WINNER = 'winner',
  WINNER_WITH_DRAW = 'winner_with_draw',
  WINNER_MARGIN = 'winner_margin',
  WINNER_MARGIN_WITH_DRAW = 'winner_margin_with_draw',
  WINNER_LOW_SCORE = 'winner_low_score',
  WINNER_LOW_SCORE_WITH_DRAW = 'winner_low_score_with_draw',
  TOTAL_GOALS_BANDS = 'total_goals_bands',
  FIRST_TO_SCORE = 'first_to_score',
  FIRST_TO_SCORE_X = 'first_to_score_x',
  FIRST_TO_SCORE_WITH_NONE = 'first_to_score_with_none',
  FIRST_TO_SCORE_X_WITH_NONE = 'first_to_score_x_with_none',
  STATS_SIMPLE = 'stats_simple', // Home/Away to score x, Total points over under
  PERIOD_SCORES = 'period_scores', // Win half, win 3rd quarter, winning at half wins
  PERIOD_SCORES_3Q_EXCL = 'period_scores_3q_excl', // Win half, winning at half wins
  PERIOD_SCORES_HOME = 'period_scores_home', // Win half, win 3rd quarter, winning at half wins for home team
  PERIOD_SCORES_AWAY = 'period_scores_away', // Win half, win 3rd quarter, winning at half wins for away team
  HALF_SCORES = 'half_scores',
  HALF_SCORES_DRAW = 'half_scores_draw',
  HALF_SCORES_HOME = 'half_scores_home',
  HALF_SCORES_AWAY = 'half_scores_away',
  HALF_SCORES_HOME_DRAW = 'half_scores_home_draw',
  HALF_SCORES_AWAY_DRAW = 'half_scores_away_draw',
  AVERAGE_HIGH_SCORER = 'average_high_scorer', // High scorers based on season average
  LAST_HIGH_SCORER = 'last_high_scorer', // High scorers based on last game
  HOME_ABOVE_AVERAGE_SCORERS = 'home_above_average_scorers', // Above season average scorers
  AWAY_ABOVE_AVERAGE_SCORERS = 'away_above_average_scorers', // Above season average scorers
  FAN_MVP = 'fan_mvp',
  HOME_HIGH_SCORER = 'home_high_scorer', // High scorer for home team
  AWAY_HIGH_SCORER = 'away_high_scorer', // High scorer for away team
  HOME_TRY_SCORER = 'home_try_scorer', // Try scorers for home team (multi-resolve)
  AWAY_TRY_SCORER = 'away_try_scorer', // Try scorers for away team (multi-resolve)
  TEAM_TRY_SCORER = 'team_try_scorer', // Try scorers for comp's team — auto home/away (multi-resolve)
  // Football's equivalent of TEAM_TRY_SCORER. A separate type rather than a
  // reused one because the name reaches the operator and the caption, and a
  // football question labelled "try scorer" is wrong in the place a player
  // would see it.
  TEAM_GOAL_SCORER = 'team_goal_scorer', // Goal scorers for comp's team — auto home/away (multi-resolve)
  DYNAMIC = 'dynamic',
}
export const TCompQuestionTemplateType = Type.Enum(CompQuestionTemplateType, {
  default: CompQuestionTemplateType.DEFAULT,
});

export const TDynamicResolutionRange = Type.Object({
  min: Type.Number(),
  max: Type.Number(),
  answerIndex: Type.Number(),
});
export type TDynamicResolutionRange = Static<typeof TDynamicResolutionRange>;

export const TDynamicResolutionRule = Type.Object({
  source: Type.String(),
  playerId: Type.Optional(Type.String()),
  stat: Type.Optional(Type.String()),
  team: Type.Optional(Type.String()),
  operator: Type.Optional(Type.String()),
  value: Type.Optional(Type.Number()),
  trueAnswer: Type.Optional(Type.Number()),
  falseAnswer: Type.Optional(Type.Number()),
  ranges: Type.Optional(Type.Array(TDynamicResolutionRange)),
});
export type TDynamicResolutionRule = Static<typeof TDynamicResolutionRule>;

/**
 * Which source graded a question, recorded at resolution time.
 *
 * Optional because questions resolved before provenance existed have no value
 * to record, and questions resolved by hand never will.
 */
export const TQuestionResolvedBy = Type.Object({
  /** Canonical resolution source, e.g. "rugbypass-match-data". */
  source: Type.String(),
  /** Adapter key, matching the entries in a player's `providerIds`. */
  providerKey: Type.String(),
  /**
   * `degraded` means the grading source attributes by name rather than a
   * stable id, or supplies only a partial stat set.
   */
  confidence: Type.Union([Type.Literal('exact'), Type.Literal('degraded')]),
  resolvedAt: TTimestamp,
});
export type TQuestionResolvedBy = Static<typeof TQuestionResolvedBy>;

export const TCompQuestion = Type.Object({
  type: Type.String(),
  name: Type.String(),
  color: Type.String(),
  hint: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  question: Type.String(),
  answers: Type.Array(TCompAnswer),
  media: TMediaEntry,
  author: TCompQuestionAuthor,
  opens: Type.Optional(TTimestamp),
  starts: Type.Optional(TTimestamp),
  link: Type.Union([TCompQuestionLink, Type.Null()]),
  status: TQuestionStatus,
  resolved: Type.Union([Type.Number(), Type.Null(), Type.Array(Type.Number())]),
  publishingType: Type.Optional(TCompQuestionPublishingType),
  templateType: TCompQuestionTemplateType,
  answersOpen: Type.Optional(Type.Boolean()),
  autoResolveDisabled: Type.Optional(Type.Boolean()),
  resolutionRule: Type.Optional(TDynamicResolutionRule),
  resolvedBy: Type.Optional(TQuestionResolvedBy),
  accentColor: Type.Optional(Type.String()),
});
export type TCompQuestion = Static<typeof TCompQuestion>;

export const TAiPrompt = Type.Object({
  created: TTimestamp,
  updated: TTimestamp,
  prompt: Type.String(),
  cacheId: Type.Optional(Type.String()),
  cacheExpireTime: Type.Optional(Type.String()),
  cachePromptHash: Type.Optional(Type.String()),
});
export type TAiPrompt = Static<typeof TAiPrompt>;

export enum CompQuestionType {
  MULTIANSWER = 'multianswer',
  MULTICHOICE = 'multichoice',
  POLL = 'poll',
  TRIVIA = 'trivia',
  SURVEY = 'survey',
  SURVEY0 = 'survey0pts',
}
export const TCompQuestionType = Type.Enum(CompQuestionType, {
  default: CompQuestionType.MULTIANSWER,
});

export const QUESTION_MISSED_ANSWER = -1;

export const TCompEntryAnswers = Type.Intersect([
  Type.Record(Type.String(), Type.Number()),
  Type.Object({length: Type.Optional(Type.Never())}),
]);
export type TCompEntryAnswers = Static<typeof TCompEntryAnswers>;

export const TCompEntryLikes = Type.Intersect([
  Type.Record(Type.String(), Type.Boolean()),
  Type.Object({length: Type.Optional(Type.Never())}),
]);
export type TCompEntryLikes = Static<typeof TCompEntryLikes>;

export const TCompEntry = Type.Object({
  created: Type.Optional(TTimestamp),
  updated: Type.Optional(TTimestamp),
  userId: Type.String(),
  compId: Type.String(),
  answers: TCompEntryAnswers,
  likes: TCompEntryLikes,
  attId: Type.Optional(Type.String()),
  verifiedScore: Type.Optional(Type.Number()),
});
export type TCompEntry = Static<typeof TCompEntry>;

export const TCompAnswerBreakdown = Type.Record(Type.String(), Type.Number());
export type TCompAnswerBreakdown = Static<typeof TCompAnswerBreakdown>;

export const TCompResolvedQuestion = Type.Object({
  id: Type.String(),
  resolved: Type.Union([Type.Number(), Type.Array(Type.Number())]),
});
export type TCompResolvedQuestion = Static<typeof TCompResolvedQuestion>;

export const TCompCounts = Type.Object({
  numQuestions: Type.Number(),
  entriesCount: Type.Number(),
  answersBreakdown: Type.Record(Type.String(), TCompAnswerBreakdown),
  likesBreakdown: Type.Record(Type.String(), Type.Number()),
  resolvedQuestions: Type.Array(TCompResolvedQuestion),
  resolutions: Type.Optional(Type.Array(TCompResolvedQuestion)),
});
export type TCompCounts = Static<typeof TCompCounts>;

export const TCompStatusUpdated = Type.Object({
  status: TCompStatus,
  updated: TTimestamp,
});
export type TCompStatusUpdated = Static<typeof TCompStatusUpdated>;

export const TTempEntry = Type.Object({
  answers: TCompEntryAnswers,
});
export type TTempEntry = Static<typeof TTempEntry>;

export const TLeaderboard = Type.Composite([
  TLeaderboardBase,
  Type.Object({
    vendor: Type.String(),
    details: Type.String(),
    comps: Type.Array(Type.String()),
    levels: Type.Array(TLeaderboardLevel),
    publishUserLeaders: Type.Boolean(),
  }),
]);
export type TLeaderboard = Static<typeof TLeaderboard>;

export const TMessage = Type.Object({
  created: TTimestamp,
  expireAt: Type.Optional(TTimestamp),
  userId: Type.String(),
  sender: Type.String(),
  message: Type.String(),
  iconUrl: Type.Union([Type.String(), Type.Null()]),
});
export type TMessage = Static<typeof TMessage>;

export enum ChatMessageType {
  UNKNOWN = -1,
  DEFAULT = 0,
  SYSTEM_DEFAULT = 1,
  SYSTEM_NEW_COMP = 2,
  SYSTEM_NEW_QUESTION = 3,
  SYSTEM_RESOLVED_QUESTION = 4,
  SYSTEM_VERIFIED_COMP = 5,
  SYSTEM_NEW_ENTRY = 6,
  SYSTEM_PRE_START_COMP = 7,
  SYSTEM_LEADERBOARD = 8,
  SYSTEM_LEADERBOARD_UPDATE = 9,
}
export const TChatMessageType = Type.Enum(ChatMessageType, {
  default: ChatMessageType.UNKNOWN,
});

export const TChatMessagePayload = Type.Object({
  compId: Type.Optional(Type.String()),
  userIds: Type.Optional(Type.Array(Type.String())),
  questionId: Type.Optional(Type.String()),
  imageUrl: Type.Optional(Type.String()),
});
export type TChatMessagePayload = Static<typeof TChatMessagePayload>;

export const TChatMessage = Type.Composite([
  TMessage,
  Type.Object({
    isVip: Type.Boolean(),
    type: TChatMessageType,
    version: Type.Optional(Type.Number()),
    payload: Type.Optional(TChatMessagePayload),
    summary: Type.Optional(Type.String()),
  }),
]);
export type TChatMessage = Static<typeof TChatMessage>;

export const TInboxMessage = Type.Composite([
  TMessage,
  Type.Object({
    opened: Type.Optional(TTimestamp),
    open: Type.Boolean(),
    nativeTitle: Type.Optional(Type.String()),
    nativeMessage: Type.Optional(Type.String()),
    nativeBadge: Type.Optional(Type.Boolean()),
    title: Type.String(),
  }),
]);
export type TInboxMessage = Static<typeof TInboxMessage>;

export const TAdminUser = Type.Object({
  email: Type.String(),
  role: Type.String(),
});
export type TAdminUser = Static<typeof TAdminUser>;

export enum GroupType {
  DEFAULT = 'DEFAULT',
  CHALLENGE = 'CHALLENGE',
}
export const TGroupType = Type.Enum(GroupType, {
  default: GroupType.DEFAULT,
});

const TBaseGroup = Type.Object({
  type: TGroupType,
  expired: Type.Boolean(),
  name: Type.String(),
  media: Type.Optional(TImageMediaEntry),
  description: Type.Optional(Type.String()),
  vendors: Type.Array(Type.String()),
  admins: Type.Array(Type.String()),
  users: Type.Array(Type.String()),
  created: TTimestamp,
  createdBy: Type.String(),
  updated: Type.Optional(TTimestamp),
  updatedBy: Type.Optional(Type.String()),
});
type TBaseGroup = Static<typeof TBaseGroup>;

export const TDefaultGroup = Type.Composite([
  TBaseGroup,
  Type.Object({
    type: Type.Literal(GroupType.DEFAULT),
  }),
]);
export type TDefaultGroup = Static<typeof TDefaultGroup>;

export const TChallengeGroup = Type.Composite([
  TBaseGroup,
  Type.Object({
    type: Type.Literal(GroupType.CHALLENGE),
    queryKey: Type.String(),
    competitionId: Type.String(),
    result: Type.Optional(Type.Array(Type.Number())),
    resulted: Type.Optional(TTimestamp),
  }),
]);
export type TChallengeGroup = Static<typeof TChallengeGroup>;

// You cannot use Value.Cast on a Union type, the easiest way to enforce this is to not create the TypeBox definition
export type TGroup = TDefaultGroup | TChallengeGroup;

export const TGroupCounts = Type.Object({
  messageCount: Type.Number(),
});
export type TGroupCounts = Static<typeof TGroupCounts>;

export const TGroupPrivate = Type.Object({
  key: Type.String(),
});
export type TGroupPrivate = Static<typeof TGroupPrivate>;

export const TOpenGraph = Type.Object({
  url: Type.String(),
  domain: Type.String(),
  title: Type.Optional(Type.String()),
  description: Type.Optional(Type.String()),
  image: Type.Optional(Type.String()),
  imageBase64: Type.Optional(Type.String()),
  siteName: Type.Optional(Type.String()),
  icon: Type.Optional(Type.String()),
  socialEmbed: Type.Optional(Type.String()),
  socialEmbedError: Type.Optional(Type.String()),
  captions: Type.Optional(Type.Array(Type.String())),
  tags: Type.Optional(Type.Array(Type.String())),
  published: Type.Optional(TTimestamp),
});
export type TOpenGraph = Static<typeof TOpenGraph>;

export const TPublishedCompAnswer = Type.Composite([
  TCompAnswer,
  Type.Object({
    count: Type.Number(),
    percentage: Type.Number(),
  }),
]);

export type TPublishedCompAnswer = Static<typeof TPublishedCompAnswer>;

export const TPublishedCompQuestion = Type.Composite([
  Type.Omit(TCompQuestion, [
    'author',
    'answers',
    'resolved',
    'color',
    'hint',
    'link',
    'media',
    'starts',
    'opens',
    'templateType',
  ]),
  Type.Object({
    answers: Type.Array(TPublishedCompAnswer),
  }),
]);
export type TPublishedCompQuestion = Static<typeof TPublishedCompQuestion>;

export const TPublishedComp = Type.Composite([
  Type.Omit(TCompBase, [
    'promo',
    'media',
    'logo',
    'sponsor',
    'closes',
    'featured',
    'starts',
    'strapline',
    'primaryColor',
    'secondaryColor',
    'teamColor',
    'shortName',
  ]),
  Type.Object({
    shortUrl: Type.String(),
    published: Type.Boolean(),
    qrCode: Type.Optional(Type.String()),
    status: TCompStatus,
    entriesCount: Type.Number(),
    description: Type.String(),
    prizeTitle: Type.String(),
    prizeImageUrl: Type.String(),
    questions: Type.Array(TPublishedCompQuestion),
  }),
]);
export type TPublishedComp = Static<typeof TPublishedComp>;

export const TAnnouncement = Type.Object({
  created: TTimestamp,
  starts: TTimestamp,
  ends: TTimestamp,
  displayCount: Type.Number({default: 1}),
  color: Type.String(),
  title: Type.String(),
  media: TMediaEntry,
});
export type TAnnouncement = Static<typeof TAnnouncement>;

export enum MediaLibraryItemStatus {
  PENDING = 'PENDING',
  READY = 'READY',
  ERROR = 'ERROR',
}
export const TMediaLibraryItemStatus = Type.Enum(MediaLibraryItemStatus, {
  default: MediaLibraryItemStatus.PENDING,
});

const TMediaLibraryItemBase = Type.Object({
  status: TMediaLibraryItemStatus,
  url: Type.String(),
  created: TTimestamp,
});

export const TMediaLibraryItemPending = Type.Composite([
  TMediaLibraryItemBase,
  Type.Object({
    status: Type.Literal(MediaLibraryItemStatus.PENDING),
  }),
]);
export type TMediaLibraryItemPending = Static<typeof TMediaLibraryItemPending>;

export const TMediaLibraryItemReady = Type.Composite([
  TMediaLibraryItemBase,
  TOpenGraph,
  Type.Object({
    status: Type.Literal(MediaLibraryItemStatus.READY),
    imageThumb: Type.Optional(Type.String()),
    imageSquare: Type.Optional(Type.String()),
    imagePortrait: Type.Optional(Type.String()),
    imageContained: Type.Optional(Type.String()),
    context: Type.Optional(
      Type.Object({
        embedJson: Type.Optional(Type.String()),
        title: Type.Optional(Type.String()),
        description: Type.Optional(Type.String()),
        readableText: Type.Optional(Type.String()),
      }),
    ),
  }),
]);
export type TMediaLibraryItemReady = Static<typeof TMediaLibraryItemReady>;

export const TMediaLibraryItemError = Type.Composite([
  TMediaLibraryItemBase,
  Type.Object({
    status: Type.Literal(MediaLibraryItemStatus.ERROR),
    error: Type.String(),
  }),
]);
export type TMediaLibraryItemError = Static<typeof TMediaLibraryItemError>;

export type TMediaLibraryItem =
  | TMediaLibraryItemPending
  | TMediaLibraryItemError
  | TMediaLibraryItemReady;

export const TNewsItem = Type.Object({
  created: TTimestamp,
  url: Type.String(),
  domain: Type.String(),
  siteName: Type.Optional(Type.String()),
  icon: Type.Optional(Type.String()),
  title: Type.Optional(Type.String()),
  imageThumb: Type.Optional(Type.String()),
  imageContained: Type.Optional(Type.String()),
  socialEmbed: Type.Optional(Type.String()),
  tags: Type.Optional(Type.Array(Type.String())),
});
export type TNewsItem = Static<typeof TNewsItem>;
